/**
 * Copyright © 2025 Delicious AI, LLC
 *
 */

import { useQuery } from '@apollo/client'
import React from 'react'
import { Navigate } from 'react-router-dom'

import {
  DAIPermissionCodename,
  GET_TENANT_STORES,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
} from '@/graphql'
import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import { usePermissions } from '@/hooks/usePermissions'

import { useAccountSetUp } from '../account-set-up/use-account-set-up'

function PermissionGuard({
  children,
  requiredPermissions,
  navigateTo = 'error/not-authorized',
}: {
  children?: React.ReactNode
  requiredPermissions: DAIPermissionCodename[]
  navigateTo?: string
}) {
  const auth = useAuth()
  const loggedInUser = useLoggedInUser()
  const tenantStores = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    variables: {
      pagination: {
        offset: 0,
        limit: 10,
      },
    },
  })
  const { loading } = useAccountSetUp({ auth, loggedInUser, tenantStores })
  const { permissions } = usePermissions()

  if (!auth.isInitialized || loading) {
    return null
  }

  const hasRequiredPermissions = requiredPermissions.every(
    permission => permissions[permission],
  )
  if (!hasRequiredPermissions) {
    return (
      <Navigate to={navigateTo} state={{ from: location.pathname }} replace />
    )
  }

  return <>{children}</>
}

export default PermissionGuard
